import SEOHead from '@/components/SEOHead';
import {
  AudioOutlined,
  BulbOutlined,
  CheckCircleFilled,
  CloudUploadOutlined,
  CodeOutlined,
  CopyOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PauseCircleOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Radio,
  Spin,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import BimiProgressIndicator from '../src/components/BimiProgressIndicator';
import { applyEdgeShape } from '../src/utils/bimi/programmaticBimiConvert';

const { Dragger } = Upload;
const { Title } = Typography;
const { Panel } = Collapse;

// Step 1: Upload Component
const UploadStep = ({ onUpload, isLoading }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="step-container"
  >
    <Card
      title={
        <span style={{ fontSize: '20px' }}>
          <CloudUploadOutlined style={{ color: '#247bfe', marginRight: '8px' }} />
          Upload Your Logo
        </span>
      }
      className="ant-card ant-card-bordered card summary-card"
    >
      <Dragger
        name="logo"
        multiple={false}
        accept=".svg"
        beforeUpload={(file) => {
          onUpload(file);
          return false;
        }}
        showUploadList={false}
        style={{
          padding: '32px',
          borderWidth: '2px',
          borderRadius: '16px',
          backgroundColor: '#fafafa',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          {isLoading ? (
            <>
              <Spin size="large" style={{ marginBottom: '24px' }} />
              <p className="upload-text">Validating logo...</p>
            </>
          ) : (
            <>
              <UploadOutlined
                style={{ fontSize: '64px', marginBottom: '24px', color: '#247bfe' }}
              />
              <p className="upload-text">Click or drag your SVG logo file here to validate</p>
              <p className="upload-hint">Must be an SVG file format</p>
            </>
          )}
        </div>
      </Dragger>
    </Card>
  </motion.div>
);

// Step 2: Validation Result Component
const ValidationStep = ({
  logoPreview,
  validationResult,
  originalCode,
  onFixClick,
  isFixing,
  onStartOver,
}) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="step-container"
  >
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '20px' }}>
            <CloudUploadOutlined style={{ color: '#247bfe', marginRight: '8px' }} />
            Logo Preview
          </span>
          <Button onClick={onStartOver} icon={<UploadOutlined />} style={{ marginLeft: '16px' }}>
            Start Over
          </Button>
        </div>
      }
      className="ant-card ant-card-bordered card summary-card"
    >
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <motion.img
          src={logoPreview}
          alt="Original Logo"
          style={{
            width: '200px',
            height: '200px',
            objectFit: 'contain',
            borderRadius: '0',
            backgroundColor: 'rgba(229, 231, 235, 0.5)',
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        />

        {validationResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="validation-result"
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              marginTop: '24px',
            }}
          >
            {validationResult.valid ? (
              <CheckCircleFilled
                style={{ fontSize: '24px', color: '#05c168', marginRight: '8px' }}
              />
            ) : (
              <ExclamationCircleOutlined
                style={{ fontSize: '24px', color: '#ff4d4f', marginRight: '8px' }}
              />
            )}
            <span
              style={{ color: validationResult.valid ? '#05c168' : '#ff4d4f', fontSize: '16px' }}
            >
              {validationResult.message}
            </span>
          </motion.div>
        )}

        {!validationResult?.valid && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={{ marginTop: '32px' }}
          >
            <Button type="primary" onClick={onFixClick} loading={isFixing} size="large">
              Make My Logo BIMI Compliant
            </Button>
          </motion.div>
        )}
      </div>

      {originalCode && (
        <Collapse
          defaultActiveKey={['1']}
          style={{ marginTop: '24px' }}
          items={[
            {
              key: '1',
              label: (
                <span style={{ fontSize: '16px', fontWeight: 500 }}>
                  <CodeOutlined style={{ marginRight: '8px' }} />
                  Original SVG Code
                </span>
              ),
              children: (
                <div style={{ position: 'relative' }}>
                  <CopyButton code={originalCode} />
                  <SyntaxHighlighter
                    language="xml"
                    style={coy}
                    customStyle={{ padding: '16px', borderRadius: '8px' }}
                  >
                    {originalCode}
                  </SyntaxHighlighter>
                </div>
              ),
            },
          ]}
        />
      )}
    </Card>
  </motion.div>
);

// Step 3: Result Component
const ResultStep = ({
  originalLogo,
  optimizedLogo,
  suggestedCode,
  fixesApplied,
  qualityScore,
  validationData,
  onStartOver,
  isAlreadyValid,
  originalCode,
  edgeShape,
  onEdgeShapeChange,
  getShapedPreview,
  getShapedCode,
}) => {
  const showFallback = qualityScore !== null && qualityScore < 0.7;

  const handleGetHumanHelp = () => {
    const supportUrl =
      '/contact?' +
      new URLSearchParams({
        subject: 'BIMI Logo Fix Assistance Request',
        message: `I need help with my BIMI logo conversion.\n\nQuality Score: ${qualityScore !== null ? (qualityScore * 100).toFixed(0) + '%' : 'N/A'}\n\nPlease review my logo and provide assistance.`,
        type: 'logo-fix-assistance',
      }).toString();
    window.open(supportUrl, '_blank');
    message.info('Opening support form...');
  };

  const handleDownload = () => {
    const codeToDownload = isAlreadyValid ? originalCode : getShapedCode();
    if (!codeToDownload) {
      message.error('No SVG code available for download');
      return;
    }
    const blob = new Blob([codeToDownload], { type: 'image/svg+xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const shapeSuffix = !isAlreadyValid && edgeShape !== 'square' ? `-${edgeShape}` : '';
    a.download = `bimi-compliant-logo${shapeSuffix}.svg`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    message.success('Logo downloaded successfully!');
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="step-container"
    >
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '20px' }}>
              <CheckCircleFilled style={{ color: '#05c168', marginRight: '8px' }} />
              {isAlreadyValid ? 'Validation Complete' : 'Optimization Complete'}
            </span>
            <Button onClick={onStartOver} icon={<UploadOutlined />} style={{ marginLeft: '16px' }}>
              Start Over
            </Button>
          </div>
        }
        className="ant-card ant-card-bordered card summary-card"
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          {isAlreadyValid ? (
            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ marginBottom: '16px', color: '#05c168' }}>
                Your Logo is BIMI Compliant!
              </h4>
              <motion.img
                src={originalLogo}
                alt="Valid Logo"
                style={{
                  width: '200px',
                  height: '200px',
                  objectFit: 'contain',
                  borderRadius: '0',
                  backgroundColor: 'rgba(229, 231, 235, 0.5)',
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              />

              {/* Show code section for valid logos */}
              <Collapse
                defaultActiveKey={['1']}
                style={{ marginTop: '32px' }}
                items={[
                  {
                    key: '1',
                    label: (
                      <span style={{ fontSize: '16px', fontWeight: 500 }}>
                        <CodeOutlined style={{ marginRight: '8px' }} />
                        BIMI Compliant SVG Code
                      </span>
                    ),
                    children: (
                      <div style={{ position: 'relative' }}>
                        <CopyButton code={originalCode} />
                        <SyntaxHighlighter
                          language="xml"
                          style={coy}
                          customStyle={{ padding: '16px', borderRadius: '8px' }}
                        >
                          {originalCode}
                        </SyntaxHighlighter>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '48px',
                  marginBottom: '24px',
                }}
              >
                <div>
                  <h4 style={{ marginBottom: '16px', color: '#666' }}>Original</h4>
                  <motion.img
                    src={originalLogo}
                    alt="Original Logo"
                    style={{
                      width: '150px',
                      height: '150px',
                      objectFit: 'contain',
                      borderRadius: '0',
                      backgroundColor: 'rgba(229, 231, 235, 0.5)',
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                  />
                </div>
                <div>
                  <h4 style={{ marginBottom: '16px', color: '#05c168' }}>
                    Optimized
                    {qualityScore !== null && (
                      <span
                        style={{
                          marginLeft: '8px',
                          fontSize: '14px',
                          fontWeight: 'normal',
                          color: qualityScore >= 0.7 ? '#05c168' : '#faad14',
                        }}
                      >
                        ({(qualityScore * 100).toFixed(0)}%)
                      </span>
                    )}
                  </h4>
                  <motion.img
                    src={getShapedPreview()}
                    alt="Optimized Logo"
                    style={{
                      width: '150px',
                      height: '150px',
                      objectFit: 'contain',
                      borderRadius: '0',
                      backgroundColor: 'rgba(229, 231, 235, 0.5)',
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  />
                </div>
              </div>

              {/* Edge Shape Selector */}
              <div style={{ marginBottom: '24px' }}>
                <div
                  style={{
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ fontWeight: 500, color: '#374151' }}>Edge Shape</span>
                  <Tooltip
                    title="Match the established shape of your logo. The entity that verifies your logo requires visual consistency with your brand's historical or trademarked appearance."
                    placement="top"
                  >
                    <QuestionCircleOutlined
                      style={{ color: '#94a3b8', cursor: 'help', fontSize: 14 }}
                    />
                  </Tooltip>
                </div>
                <Radio.Group
                  value={edgeShape}
                  onChange={(e) => onEdgeShapeChange(e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value="square">Square</Radio.Button>
                  <Radio.Button value="rounded">Rounded</Radio.Button>
                  <Radio.Button value="circle">Circle</Radio.Button>
                </Radio.Group>
              </div>

              {/* Quality Warning Banner */}
              {showFallback && (
                <Alert
                  message="Results may need review"
                  description={
                    <div>
                      <p style={{ marginBottom: '8px' }}>
                        The AI fixed your logo, but the quality score is{' '}
                        {(qualityScore * 100).toFixed(0)}% (target: 70%+).
                        {validationData &&
                          !validationData.isValid &&
                          validationData.errors?.length > 0 && (
                            <span> Some validation issues remain.</span>
                          )}
                      </p>
                      <Button
                        type="primary"
                        size="small"
                        icon={<QuestionCircleOutlined />}
                        onClick={handleGetHumanHelp}
                        style={{ marginTop: '8px' }}
                      >
                        Get help from a human expert
                      </Button>
                    </div>
                  }
                  type="warning"
                  icon={<WarningOutlined />}
                  showIcon
                  style={{ marginBottom: '24px', textAlign: 'left' }}
                />
              )}
            </>
          )}

          <Button type="primary" icon={<DownloadOutlined />} size="large" onClick={handleDownload}>
            Download BIMI Compliant SVG
          </Button>
        </div>

        {/* AI Improvements Made - Using fixesApplied array */}
        {!isAlreadyValid && fixesApplied && fixesApplied.length > 0 && (
          <Collapse
            defaultActiveKey={['1']}
            style={{ marginTop: '32px' }}
            items={[
              {
                key: '1',
                label: (
                  <span style={{ fontSize: '16px', fontWeight: 500 }}>
                    <BulbOutlined style={{ marginRight: '8px', color: '#247bfe' }} />
                    AI Improvements Made ({fixesApplied.length})
                  </span>
                ),
                children: (
                  <div>
                    {fixesApplied.map((fix, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          marginBottom: '12px',
                          padding: '12px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                        }}
                      >
                        <CheckCircleFilled
                          style={{ color: '#05c168', marginRight: '12px', marginTop: '4px' }}
                        />
                        <span>{fix}</span>
                      </motion.div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        )}

        {/* Quality Details */}
        {!isAlreadyValid && qualityScore !== null && (
          <Collapse
            style={{ marginTop: '16px' }}
            items={[
              {
                key: 'quality',
                label: (
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>
                    Quality Details ({(qualityScore * 100).toFixed(0)}%)
                  </span>
                ),
                children: (
                  <div style={{ fontSize: '13px' }}>
                    {validationData && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Validation:</strong>{' '}
                        {validationData.isValid ? (
                          <span style={{ color: '#05c168' }}>✓ Passed</span>
                        ) : (
                          <span style={{ color: '#ff4d4f' }}>✗ Issues detected</span>
                        )}
                        {validationData.errors && validationData.errors.length > 0 && (
                          <ul style={{ marginTop: '8px', marginLeft: '20px', listStyle: 'disc' }}>
                            {validationData.errors.map((err, idx) => (
                              <li
                                key={idx}
                                style={{
                                  color: '#dc2626',
                                  fontSize: '13px',
                                  lineHeight: '1.5',
                                  marginBottom: '4px',
                                }}
                              >
                                {err}
                              </li>
                            ))}
                          </ul>
                        )}
                        {validationData.warnings && validationData.warnings.length > 0 && (
                          <ul style={{ marginTop: '8px', marginLeft: '20px', listStyle: 'disc' }}>
                            {validationData.warnings.map((warn, idx) => (
                              <li
                                key={idx}
                                style={{
                                  color: '#d97706',
                                  fontSize: '13px',
                                  lineHeight: '1.5',
                                  marginBottom: '4px',
                                }}
                              >
                                {warn}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}

        {/* SVG Code Section */}
        {!isAlreadyValid && suggestedCode && (
          <Collapse
            defaultActiveKey={['1']}
            style={{ marginTop: '16px' }}
            items={[
              {
                key: '1',
                label: (
                  <span style={{ fontSize: '16px', fontWeight: 500 }}>
                    <CodeOutlined style={{ marginRight: '8px' }} />
                    BIMI Compliant SVG Code
                  </span>
                ),
                children: (
                  <div style={{ position: 'relative' }}>
                    <CopyButton code={getShapedCode()} />
                    <SyntaxHighlighter
                      language="xml"
                      style={coy}
                      customStyle={{ padding: '16px', borderRadius: '8px' }}
                    >
                      {getShapedCode()}
                    </SyntaxHighlighter>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>
    </motion.div>
  );
};

// Copy Button Component
const CopyButton = ({ code }) => {
  const [copied, setCopied] = useState(false);

  return (
    <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>
      {copied && (
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          style={{
            marginRight: '8px',
            color: '#05c168',
            fontSize: '14px',
          }}
        >
          Copied!
        </motion.span>
      )}
      <Button
        icon={<CopyOutlined />}
        size="small"
        onClick={() => {
          navigator.clipboard.writeText(code);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      />
    </div>
  );
};

const BimiLogoAIWizard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [originalCode, setOriginalCode] = useState('');
  const [suggestedCode, setSuggestedCode] = useState('');
  const [isFixing, setIsFixing] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioRef = useRef();
  const [messageIndex, setMessageIndex] = useState(0);
  const loadingMessages = [
    'Processing your logo...',
    'Assessing logo design...',
    'Analyzing vector details...',
    'Optimizing color palette...',
    'Finalizing BIMI compliance...',
  ];
  const [currentStep, setCurrentStep] = useState(1);
  const [fixedLogoPreview, setFixedLogoPreview] = useState(null);

  // New state for enhanced features
  const [edgeShape, setEdgeShape] = useState('square');
  const [qualityScore, setQualityScore] = useState(null);
  const [fixesApplied, setFixesApplied] = useState([]);
  const [validationData, setValidationData] = useState(null);

  // Helper to create data URI from SVG
  const svgToDataURL = (svg) => {
    if (!svg) return null;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  // Get SVG with edge shape applied
  const getShapedCode = () => {
    if (!suggestedCode) return null;
    return applyEdgeShape(suggestedCode, edgeShape);
  };

  // Get preview with edge shape applied
  const getShapedPreview = () => {
    const shapedCode = getShapedCode();
    return shapedCode ? svgToDataURL(shapedCode) : fixedLogoPreview;
  };

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/audio/brandymail-bimi-logo-ai-wizard.mp3');
      audioRef.current.addEventListener('ended', () => {
        setIsAudioPlaying(false);
      });
    }
  }, []);

  useEffect(() => {
    let intervalId;
    if (isFixing) {
      intervalId = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
    } else {
      setMessageIndex(0);
    }
    return () => clearInterval(intervalId);
  }, [isFixing]);

  const handleToggleAudio = () => {
    if (!isAudioPlaying) {
      audioRef.current.play();
      setIsAudioPlaying(true);
    } else {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    }
  };

  const handleLogoUpload = async (file) => {
    setIsLoading(true);
    setCurrentStep(1);
    setValidationResult(null);
    setLogoPreview(null);
    setSuggestedCode('');
    setIsFixing(false);
    setFixedLogoPreview(null);
    // Reset new state
    setEdgeShape('square');
    setQualityScore(null);
    setFixesApplied([]);
    setValidationData(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const svgContent = e.target.result;
        console.log('SVG Content loaded:', { length: svgContent.length });

        // Set original code first, before any other operations
        setOriginalCode(svgContent);

        const dataURI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
        setLogoPreview(dataURI);

        // Validate SVG content locally
        if (
          !svgContent
            .trim()
            .replace(/^<\?xml.*?\?>/, '')
            .trim()
            .startsWith('<svg')
        ) {
          setValidationResult({
            valid: false,
            type: 'INPUT_ERROR',
            message: 'Invalid SVG format - file must start with <svg> tag',
          });
          setIsLoading(false);
          return;
        }

        // Call the validation endpoint
        const response = await axios.post(
          '/api/validateBimiLogo',
          { svgContent },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        console.log('Validation response:', response.data);

        // Handle the validation result
        setValidationResult(response.data);

        if (response.data.valid) {
          // For valid logos, use the original code
          setSuggestedCode(svgContent);
          setCurrentStep(3);
          setTimeout(() => {
            confetti({
              particleCount: 150,
              spread: 80,
              origin: { y: 0.6 },
            });
          }, 100);
        } else {
          setCurrentStep(2);
        }
      } catch (error) {
        console.error('Error validating logo:', error);
        setValidationResult({
          valid: false,
          type: error.response?.data?.type || 'SYSTEM_ERROR',
          message: error.response?.data?.message || 'Unable to validate logo. Please try again.',
        });
      } finally {
        setIsLoading(false);
      }
    };
    reader.readAsText(file);
  };

  const handleFixLogo = async () => {
    setIsFixing(true);
    let conversionSuccessful = false;
    try {
      const response = await axios.post(
        '/api/fixBimiLogo',
        { svgCode: originalCode },
        {
          timeout: 70000, // 70 second timeout (API has 55s, allow extra time for network)
        }
      );
      if (response.data && response.data.suggestedCode) {
        conversionSuccessful = true;
        setSuggestedCode(response.data.suggestedCode);

        // Store fixes applied array for detailed display
        if (response.data.fixesApplied && Array.isArray(response.data.fixesApplied)) {
          setFixesApplied(response.data.fixesApplied);
        }

        // Store quality and validation data
        if (response.data.qualityScore !== undefined) {
          setQualityScore(response.data.qualityScore);
        }
        if (response.data.validation) {
          setValidationData(response.data.validation);
        }

        // Create preview of fixed logo
        const fixedDataURI = svgToDataURL(response.data.suggestedCode);
        setFixedLogoPreview(fixedDataURI);
        setCurrentStep(3);

        // Show warning if quality is low
        if (response.data.qualityScore !== undefined && response.data.qualityScore < 0.7) {
          message.warning(
            'Logo fixed, but results may need review. Consider requesting human assistance if needed.'
          );
        } else {
          message.success('Logo fixed successfully!');
        }
      } else {
        message.error('No fixed logo was generated. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching suggested code:', error);
      if (error.response) {
        if (error.response.status === 429) {
          const retryAfter = error.response.headers['retry-after'] || 60;
          message.error(
            `Please wait ${retryAfter} seconds before trying again. We limit requests to protect our AI service.`
          );
        } else if (error.response.status === 504 || error.response.data?.code === 'TIMEOUT') {
          const errorMessage =
            error.response.data?.message || 'Request timed out. Please try again.';
          message.error(errorMessage);
        } else if (error.response.status === 500) {
          message.error('An error occurred while processing your logo. Please try again later.');
        } else if (error.response.status === 400) {
          // Check for detailed error messages (e.g., gradient not supported)
          const details = error.response.data?.details;
          let errorMessage;

          if (Array.isArray(details) && details.length > 0) {
            errorMessage = details[0];
          } else {
            errorMessage =
              error.response.data?.message ||
              error.response.data?.error ||
              'Bad Request. Please ensure your SVG is valid.';
          }
          message.error(errorMessage);
        } else {
          const errorMessage =
            error.response.data?.message ||
            error.response.data?.error ||
            'An error occurred while fixing the logo.';
          message.error(errorMessage);
        }
      } else if (error.code === 'ECONNABORTED') {
        message.error(
          'Processing your logo is taking longer than expected. Our AI is working on optimizing it - please try again in a moment.'
        );
      } else {
        message.error(
          'Unable to connect to the server. Please check your connection and try again.'
        );
      }
    } finally {
      setIsFixing(false);
      if (conversionSuccessful) {
        setTimeout(() => {
          confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 },
          });
        }, 100);
      }
    }
  };

  const handleStartOver = () => {
    setCurrentStep(1);
    setLogoPreview(null);
    setOriginalCode('');
    setSuggestedCode('');
    setValidationResult(null);
    setFixedLogoPreview(null);
    // Reset new state
    setEdgeShape('square');
    setQualityScore(null);
    setFixesApplied([]);
    setValidationData(null);
  };

  return (
    <div className="bimi-logo-ai-wizard">
      <SEOHead />
      <section
        className="products section hero v4"
        style={{ paddingTop: '132px', paddingBottom: '0' }}
      >
        <div className="container-default w-container">
          <div className="z-index-1">
            <div className="mg-bottom-24px">
              <div className="inner-container center">
                <div className="inner-container _861px---tablet center">
                  <div className="text-center">
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        <p>Turn Your Logo Into a Trust Mark</p>
                      </div>
                    </div>
                    <h1 className="mg-bottom-16px">BIMI Logo AI Wizard</h1>
                    <div className="inner-container center" style={{ maxWidth: '680px' }}>
                      <p className="color-neutral-600 mg-bottom-0">
                        Transform your logo into a BIMI-compliant trust mark with our AI-powered
                        wizard. Upload your SVG logo and get instant recommendations to meet BIMI
                        requirements.
                        <Tooltip title={isAudioPlaying ? 'Pause audio' : 'Play audio'}>
                          <span
                            onClick={handleToggleAudio}
                            style={{ cursor: 'pointer', marginLeft: '4px' }}
                          >
                            {isAudioPlaying ? (
                              <PauseCircleOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                            ) : (
                              <AudioOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                            )}
                          </span>
                        </Tooltip>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="bg-neutral-100" style={{ paddingTop: '0', paddingBottom: '100px' }}>
        <div className="container-default w-container">
          <div className="inner-container" style={{ maxWidth: '680px', margin: '0 auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}
            >
              <BimiProgressIndicator
                currentStep={currentStep}
                validationFailed={validationResult && !validationResult.valid}
              />
            </motion.div>

            <AnimatePresence mode="wait">
              {currentStep === 1 && (
                <UploadStep key="upload" onUpload={handleLogoUpload} isLoading={isLoading} />
              )}
              {currentStep === 2 && (
                <ValidationStep
                  key="validate"
                  logoPreview={logoPreview}
                  validationResult={validationResult}
                  originalCode={originalCode}
                  onFixClick={handleFixLogo}
                  isFixing={isFixing}
                  onStartOver={handleStartOver}
                />
              )}
              {currentStep === 3 && (
                <ResultStep
                  key="result"
                  originalLogo={logoPreview}
                  optimizedLogo={fixedLogoPreview}
                  suggestedCode={suggestedCode}
                  fixesApplied={fixesApplied}
                  qualityScore={qualityScore}
                  validationData={validationData}
                  onStartOver={handleStartOver}
                  isAlreadyValid={validationResult?.valid}
                  originalCode={originalCode}
                  edgeShape={edgeShape}
                  onEdgeShapeChange={setEdgeShape}
                  getShapedPreview={getShapedPreview}
                  getShapedCode={getShapedCode}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {isFixing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="loading-overlay"
        >
          <div className="overlay-backdrop"></div>
          <div className="overlay-content">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48, fontWeight: 'bold' }} spin />}
            />
            <div className="loading-message">{loadingMessages[messageIndex]}</div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default BimiLogoAIWizard;
